/**
 * Grading / Repair submission form — multi-card. Each card carries Service Fee,
 * Pre-FMV, Grade, Post-FMV. On Status="Returned" with a Grade and Post-FMV,
 * the linked source Card lots get patched (Inventory Class → Slab, Status →
 * Available (back in inventory, sellable), Condition/Grade → the new grade, FMV updated).
 */
import { DB } from "../lib/db";
import { escapeHtml, toast, scrollItemsToBottom, $ } from "../lib/dom";
import { fmt$, fmtDate, todayISO } from "../utils/format";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { gradesForProvider, GRADING_PROVIDERS } from "../components/gradePicker";
import { wireLotSearch, type LotOption } from "../components/lotSearch";
import { mileageSectionHtml, wireMileageSection, saveMileageSection } from "../components/mileageSection";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { reverseEffect, effectForTransaction, type InvEffect } from "../lib/inventoryEffects";
import { processReceiptFile } from "../lib/receipt";
import { uploadReceiptDataUrl, deleteReceiptByPath, isStoragePath } from "../lib/storage";
import { viewReceipt } from "../components/receiptViewer";

interface GradingCard {
  "Card Lot ID"?: string; "Card Name": string;
  "Set": string; "Number": string;
  "Service Fee": number; "Pre-FMV": number;
  "Grade": string; "Post-FMV": number;
  "Notes": string;
  /** True once Service Fee + allocated shipping have been capitalized into
   *  the source card lot's basis (on first "Returned" save). Idempotency
   *  guard so re-saves don't double-add. */
  "Basis Applied"?: boolean;
  /** Dollar amount that was actually added to the source lot's basis when
   *  this card was marked Returned. Stored so we can subtract the exact
   *  same number if the submission is later rolled back. */
  "Basis Applied Amount"?: number;
}
interface GradingState {
  "Submission ID": string;
  "Date Sent": string; "Date Returned": string;
  "Status": string; "Service Types": string[];
  "Provider": string; "Total Shipping": number;
  "Invoice Number": string;
  "Cards": GradingCard[];
  "Notes": string;
  "_isNew": boolean;
}
function S(): GradingState | null { return (window as any)._gradingState as GradingState | null; }

/** In-form invoice UI (mirrors Purchases): upload / view / remove buttons. */
function renderGradingInvoiceUI(row: any): string {
  const has = !!row["Invoice File"];
  const name = row["Invoice File Name"] || "";
  return `
    <button type="button" class="btn btn-ghost" onclick="document.getElementById('gradingInvoiceInput').click()">\u{1F4CE} ${has ? 'Replace invoice' : 'Upload invoice'}</button>
    ${has ? `
      <button type="button" class="btn btn-ghost" onclick="viewGradingInvoiceFromForm()">\u{1F441} View</button>
      <button type="button" class="btn btn-ghost" onclick="clearGradingInvoice()">✕ Remove</button>
      <span class="text-xs text-[var(--muted)] truncate" style="max-width:240px">${escapeHtml(name || 'attached')}</span>
    ` : `<span class="text-xs text-[var(--muted)]">No invoice attached</span>`}`;
}

export async function editGrading(id?: string): Promise<void> {
  const isNew = !id;
  // Pull lot-bearing tables fresh so the lot search reflects cloud-current
  // qty/FMV. Also refresh grading on edit to hydrate from the latest stored row.
  {
    const refreshes: Promise<void>[] = [DB.refreshTable("sealed"), DB.refreshTable("cards")];
    if (!isNew) refreshes.push(DB.refreshTable("grading"));
    await Promise.all(refreshes);
  }
  const sub = isNew
    ? { "Submission ID": nextId("GR", "grading", "Submission ID"), "Date Sent": todayISO(),
        "Date Returned": "", "Status": "At Service",
        "Service Types": ["Grading"], "Provider": "PSA", "Total Shipping": 0, "Cards": [], "Notes": "" }
    : DB.table("grading").find(r => r["Submission ID"] === id);
  if (!sub) { toast("Submission not found", "err"); return; }

  (window as any)._gradingState = {
    "Submission ID": sub["Submission ID"],
    "Date Sent":     sub["Date Sent"],
    "Date Returned": sub["Date Returned"] || "",
    "Status":        sub["Status"] || "At Service",
    "Service Types": Array.isArray(sub["Service Types"]) ? sub["Service Types"].slice() : ["Grading"],
    "Provider":      sub["Provider"] || "",
    "Total Shipping":Number(sub["Total Shipping"]) || 0,
    "Invoice Number":sub["Invoice Number"] || "",
    "Cards":         (sub.Cards || []).map((x: any) => ({ ...x })),
    "Notes":         sub["Notes"] || "",
    "_isNew":        isNew,
  } as GradingState;

  const st = S()!;
  const isGrading = st["Service Types"].includes("Grading");
  const isRepair  = st["Service Types"].includes("Repair");
  const provPreset = ["PSA", "BGS", "CGC", "TAG"].includes(String(sub["Provider"] || ""))
    ? String(sub["Provider"])
    : (sub["Provider"] ? "Other" : "PSA");

  const body = `
    <form id="gradingForm" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        ${formField({ label: "Submission ID", name: "Submission ID", value: sub["Submission ID"], required: true })}
        ${formField({ label: "Date Sent", name: "Date Sent", type: "date", value: fmtDate(sub["Date Sent"]) })}
        <div>
          <label class="label" for="gradProviderSelect">Service Provider</label>
          <div class="flex gap-2">
            <select class="select" id="gradProviderSelect" onchange="updateGradingProvider(this.value)">
              ${GRADING_PROVIDERS.map(p => `<option value="${p}" ${provPreset === p ? "selected" : ""}>${p === "Other" ? "Other (type it in)" : p}</option>`).join("")}
            </select>
            <input class="input" name="Provider" id="gradProviderInput" value="${escapeHtml(String(sub["Provider"] || (provPreset === "Other" ? "" : provPreset)))}" placeholder="Type provider" style="${provPreset === "Other" ? "" : "display:none"}">
          </div>
        </div>
      </div>

      <div class="mb-6">
        <div class="text-xs text-[var(--muted)] mb-2">Service Type (one or both):</div>
        <label class="inline-flex items-center gap-2 mr-4 cursor-pointer">
          <input type="checkbox" id="gradSvcGrading" ${isGrading ? "checked" : ""} onchange="toggleGradingService('Grading',this.checked)">
          <span>Grading</span>
        </label>
        <label class="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" id="gradSvcRepair" ${isRepair ? "checked" : ""} onchange="toggleGradingService('Repair',this.checked)">
          <span>Repair</span>
        </label>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        ${formField({ label: "Status", name: "Status", value: sub["Status"] || "At Service", options: ["At Service", "Returned", "Listed", "Sold"] })}
        ${formField({ label: "Date Returned", name: "Date Returned", type: "date", value: fmtDate(sub["Date Returned"] || "") })}
        ${formField({ label: "Total Shipping/Insurance ($)", name: "Total Shipping", type: "number", value: sub["Total Shipping"] || 0 })}
      </div>

      <div class="mb-6">
        <div class="flex items-center justify-between mb-3 pb-2 border-b border-[var(--border)] flex-wrap gap-2">
          <h4 class="font-semibold text-violet-300">Cards in this submission</h4>
          <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addGradingCard()">+ Add Card</button>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Type below to add a card lot; press Enter without selecting to add a free-text card. Fill in <b>Grade</b> and <b>Post-FMV</b> when cards return — set Status to "Returned" on save and any linked Card lots will auto-update with the new grade and FMV.</div>
        <div id="gradingCards" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
        <div class="mt-3 flex items-stretch gap-2">
          <div class="combobox-wrapper flex-1 min-w-0" data-grading-add-search>
            <input class="input w-full" autocomplete="off" placeholder="Type to add a card to this submission…">
            <div class="combobox-list"></div>
          </div>
        </div>
      </div>

      ${formField({ label: "Notes", name: "Notes", type: "textarea", value: sub["Notes"] || "" })}

      <div class="mt-4 pt-3 border-t border-[var(--border)] grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
        <div>
          ${formField({ label: "Invoice Number", name: "Invoice Number", value: sub["Invoice Number"] || "", placeholder: "e.g. PSA invoice / order #" })}
        </div>
        <div>
          <label class="label">Invoice file (image or PDF)</label>
          <input type="hidden" name="Invoice File"      value="${escapeHtml(sub["Invoice File"] || "")}">
          <input type="hidden" name="Invoice File Name" value="${escapeHtml(sub["Invoice File Name"] || "")}">
          <input type="hidden" name="Invoice File Type" value="${escapeHtml(sub["Invoice File Type"] || "")}">
          <input type="file" id="gradingInvoiceInput" accept="image/*,application/pdf" class="hidden" onchange="onGradingInvoiceUpload(event)">
          <div id="gradingInvoiceUI" class="flex flex-wrap gap-2 items-center">${renderGradingInvoiceUI(sub)}</div>
          <div class="text-xs text-[var(--muted)] mt-2">One invoice per submission. Images are auto-compressed before upload; files are stored privately in your Supabase Storage bucket.</div>
        </div>
      </div>

      ${mileageSectionHtml(sub["Submission ID"], "Grading Drop-Off")}

      <div class="mt-4 pt-3 pb-3 -mx-4 px-4 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm sticky bottom-[68px] z-10 bg-[var(--panel)] shadow-[0_-8px_12px_-12px_rgba(0,0,0,0.6)]">
        <div><div class="text-xs text-[var(--muted)]">Cards</div><div id="gradCardCount" class="stat-num font-bold">0</div></div>
        <div><div class="text-xs text-[var(--muted)]">Service Fees</div><div id="gradTotalFees" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Pre-FMV (raw)</div><div id="gradPreFMV" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Post-FMV (graded)</div><div id="gradPostFMV" class="stat-num font-bold">$0.00</div></div>
      </div>
    </form>`;
  const safeId = escapeHtml(sub["Submission ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('grading','Submission ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveGrading('${isNew ? "" : safeId}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Grading/Repair Submission" : "Edit Grading/Repair Submission", body, footer, wide: true });

  const shipInput = document.querySelector<HTMLInputElement>('#gradingForm [name="Total Shipping"]');
  shipInput?.addEventListener("input", () => {
    const cur = S(); if (!cur) return;
    cur["Total Shipping"] = Number(shipInput.value) || 0;
    recomputeGradingTotals();
  });
  renderGradingCardsList();
  wireGradingAddSearch();
  wireMileageSection();
}

export function toggleGradingService(svc: string, on: boolean): void {
  const s = S(); if (!s) return;
  const list = s["Service Types"];
  const i = list.indexOf(svc);
  if (on && i < 0) list.push(svc);
  else if (!on && i >= 0) list.splice(i, 1);
  markModalDirty();
}

/* ----- Cards add-search at the bottom of the list ----- */
function _addGradingCardFromLot(lot: LotOption): void {
  const s = S(); if (!s) return;
  if (lot.type !== "Card") { return; }
  const itemName = (lot.label.split(" — ")[0] || lot.id).replace(/^[A-Z]+-\d+-\d+ · /, "");
  s.Cards.push({
    "Card Lot ID": lot.id,
    "Card Name":   itemName,
    "Service Fee": 0,
    "Pre-FMV":     +(Number(lot.fmv) || 0).toFixed(2),
    "Grade":       "",
    "Post-FMV":    0,
  } as any);
  renderGradingCardsList();
  markModalDirty();
  scrollItemsToBottom("gradingCards");
}

function _addGradingCardFreeText(text: string): void {
  const s = S(); if (!s) return;
  s.Cards.push({ "Card Lot ID": "", "Card Name": text, "Service Fee": 0, "Pre-FMV": 0, "Grade": "", "Post-FMV": 0 } as any);
  renderGradingCardsList();
  markModalDirty();
  scrollItemsToBottom("gradingCards");
}

function wireGradingAddSearch(): void {
  const wrapper = document.querySelector<HTMLElement>("[data-grading-add-search]");
  if (!wrapper) return;
  const inp = wrapper.querySelector<HTMLInputElement>("input");
  if (!inp) return;
  wireLotSearch(wrapper, {
    kind: "card",
    excludeLocked: true,
    excludeIds: () => {
      const set = new Set<string>();
      const cur = S();
      (cur?.Cards || []).forEach((x: any) => { if (x["Card Lot ID"]) set.add(x["Card Lot ID"]); });
      return set;
    },
    onSelect: (lot) => {
      _addGradingCardFromLot(lot);
      inp.value = "";
      inp.focus();
    },
  });
  inp.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || e.defaultPrevented) return;
    const list = wrapper.querySelector<HTMLElement>(".combobox-list");
    const hasActive = list?.querySelector(".combobox-item.active");
    const text = inp.value.trim();
    if (hasActive || !text) return;
    e.preventDefault();
    _addGradingCardFreeText(text);
    inp.value = "";
  });
}

export function addGradingCard(): void {
  const s = S(); if (!s) return;
  s.Cards.push({
    "Card Lot ID": "", "Card Name": "", "Set": "", "Number": "",
    "Service Fee": 0, "Pre-FMV": 0, "Grade": "", "Post-FMV": 0, "Notes": "",
  });
  renderGradingCardsList();
  markModalDirty();
  scrollItemsToBottom("gradingCards");
}
export function removeGradingCard(idx: number): void {
  const s = S(); if (!s) return;
  s.Cards.splice(idx, 1);
  renderGradingCardsList();
  markModalDirty();
}
export function updateGradingCardField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const c = s.Cards[idx]; if (!c) return;
  (c as any)[field] = (field === "Service Fee" || field === "Pre-FMV" || field === "Post-FMV")
    ? (Number(value) || 0) : value;
  recomputeGradingTotals();
  markModalDirty();
}

export function updateGradingProvider(val: string): void {
  const input = document.getElementById("gradProviderInput") as HTMLInputElement | null;
  if (!input) return;
  if (val === "Other") {
    input.style.display = "";
    if (["PSA", "BGS", "CGC", "TAG"].includes(input.value)) input.value = "";
    input.focus();
  } else {
    input.value = val;
    input.style.display = "none";
  }
  markModalDirty();
  renderGradingCardsList();
}

function renderGradingCardsList(): void {
  const container = document.getElementById("gradingCards");
  if (!container) return;
  const s = S(); if (!s) return;
  const cards = s.Cards;
  const provForGrades = (document.getElementById("gradProviderInput") as HTMLInputElement | null)?.value || "";
  const provGradeSet = gradesForProvider(provForGrades);
  document.getElementById("gradingCardsAddMore")?.classList.toggle("hidden", !cards.length);
  if (!cards.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No cards yet — click "+ Add Card" or type below to add a card lot.</div>`;
    recomputeGradingTotals();
    return;
  }
  container.innerHTML = cards.map((c, i) => `
    <div class="panel p-2" data-grad-card="${i}">
      <div class="flex items-center gap-2 mb-2 flex-wrap">
        <span class="pill pill-violet">Card ${i + 1}</span>
        <div class="flex-1 min-w-[220px]">
          <div class="combobox-wrapper" data-grad-lot-search="${i}">
            <input class="input !py-1 text-sm" autocomplete="off" placeholder="Search inventory or type a card name…" value="${escapeHtml(c["Card Name"] || c["Card Lot ID"] || "")}">
            <div class="combobox-list"></div>
          </div>
        </div>
        <div class="text-xs text-[var(--muted)]">${c["Card Lot ID"] ? `Linked: <code>${escapeHtml(c["Card Lot ID"])}</code>` : `<span class="text-amber-300">Not linked</span>`}</div>
        <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removeGradingCard(${i})" title="Remove">✕</button>
      </div>
      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-6 sm:col-span-3">
          <label class="label text-xs">Set</label>
          <input class="input" placeholder="Set name" value="${escapeHtml(c["Set"] || "")}" oninput="updateGradingCardField(${i},'Set',this.value)">
        </div>
        <div class="col-span-3 sm:col-span-1">
          <label class="label text-xs">Card #</label>
          <input class="input" value="${escapeHtml(c["Number"] || "")}" oninput="updateGradingCardField(${i},'Number',this.value)">
        </div>
        <div class="col-span-3 sm:col-span-2">
          <label class="label text-xs">Service Fee ($)</label>
          <input class="input" type="number" step="0.01" value="${c["Service Fee"] || 0}" oninput="updateGradingCardField(${i},'Service Fee',this.value)">
        </div>
        <div class="col-span-6 sm:col-span-2">
          <label class="label text-xs">Pre-FMV ($)</label>
          <input class="input" type="number" step="0.01" value="${c["Pre-FMV"] || 0}" oninput="updateGradingCardField(${i},'Pre-FMV',this.value)">
        </div>
        <div class="col-span-6 sm:col-span-2">
          <label class="label text-xs">Grade</label>
          <select class="select" onchange="updateGradingCardField(${i},'Grade',this.value)">
            ${(() => {
              const base = ["", ...provGradeSet, "Other"];
              const list = (c["Grade"] && !base.includes(c["Grade"])) ? ["", c["Grade"], ...provGradeSet, "Other"] : base;
              return list.map(o => `<option value="${escapeHtml(o)}" ${c["Grade"] === o ? "selected" : ""}>${escapeHtml(o || "— pending —")}</option>`).join("");
            })()}
          </select>
        </div>
        <div class="col-span-12 sm:col-span-2">
          <label class="label text-xs">Post-FMV ($)</label>
          <input class="input" type="number" step="0.01" value="${c["Post-FMV"] || 0}" oninput="updateGradingCardField(${i},'Post-FMV',this.value)">
        </div>
        <div class="col-span-12">
          <label class="label text-xs">Notes</label>
          <input class="input !py-1 text-sm" placeholder="Per-card notes" value="${escapeHtml(c["Notes"] || "")}" oninput="updateGradingCardField(${i},'Notes',this.value)">
        </div>
      </div>
    </div>`).join("");
  // Per-card lot search (cards only, qty > 0); hide already-picked lots
  cards.forEach((_card, i) => {
    const wrapper = container.querySelector<HTMLElement>(`[data-grad-lot-search="${i}"]`);
    if (!wrapper) return;
    wireLotSearch(wrapper, {
      kind: "card",
      excludeLocked: true,
      excludeIds: () => {
        const set = new Set<string>();
        const cur = S();
        (cur?.Cards || []).forEach((x, j) => { if (j !== i && x["Card Lot ID"]) set.add(x["Card Lot ID"]!); });
        return set;
      },
      onSelect: (lot) => {
        const cur = S(); if (!cur) return;
        const c = cur.Cards[i];
        c["Card Lot ID"] = lot.id;
        const src = DB.table("cards").find(r => r["Card Lot ID"] === lot.id);
        if (src) {
          c["Card Name"] = src["Card Name"] || "";
          c["Set"]       = src["Set"] || "";
          c["Number"]    = src["Card #"] || "";
          // Pre-FMV defaults to per-unit FMV of the lot
          const qty = Number(src["Qty"]) || 1;
          c["Pre-FMV"] = qty > 0 ? +(((Number(src["Current FMV"]) || 0) / qty)).toFixed(2) : 0;
        }
        renderGradingCardsList();
        markModalDirty();
      },
    });
  });
  recomputeGradingTotals();
}

function recomputeGradingTotals(): void {
  const s = S(); if (!s) return;
  const cards = s.Cards || [];
  const totalFees = cards.reduce((acc, c) => acc + (Number(c["Service Fee"]) || 0), 0);
  const totalPre  = cards.reduce((acc, c) => acc + (Number(c["Pre-FMV"]) || 0), 0);
  const totalPost = cards.reduce((acc, c) => acc + (Number(c["Post-FMV"]) || 0), 0);
  const setText = (id: string, val: string) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  setText("gradCardCount", String(cards.length));
  setText("gradTotalFees", fmt$(totalFees));
  setText("gradPreFMV",    fmt$(totalPre));
  setText("gradPostFMV",   fmt$(totalPost));
}

/** True when an edit changed a card-affecting field — status, service types,
 *  shipping, or any card's grade / post-FMV / service fee / membership. Pure
 *  meta edits (notes, provider, dates) return false and skip reverse-reapply. */
function gradingCardsChanged(existing: any, status: string, totalShipping: number, serviceTypes: string[], cards: GradingCard[]): boolean {
  if (!existing) return true;
  if ((existing["Status"] || "") !== status) return true;
  if ((Number(existing["Total Shipping"]) || 0) !== (Number(totalShipping) || 0)) return true;
  const oldST = (Array.isArray(existing["Service Types"]) ? existing["Service Types"] : []).slice().sort().join(",");
  const newST = serviceTypes.slice().sort().join(",");
  if (oldST !== newST) return true;
  const sig = (arr: any[]) => arr.map(c => `${c["Card Lot ID"] || ""}|${c["Grade"] || ""}|${Number(c["Post-FMV"]) || 0}|${Number(c["Service Fee"]) || 0}`).sort().join("~");
  const oldCards = Array.isArray(existing["Cards"]) ? existing["Cards"] : [];
  return sig(oldCards) !== sig(cards);
}

export async function saveGrading(origId: string): Promise<void> {
  const s = S(); if (!s) return;
  const formData = readForm($("#gradingForm"));
  const submissionId = (formData["Submission ID"] || s["Submission ID"] || nextId("GR", "grading", "Submission ID")).trim();
  const dateSent = formData["Date Sent"] || todayISO();
  const dateReturned = formData["Date Returned"] || "";
  const status = formData["Status"] || "At Service";
  const provider = formData["Provider"] || "";
  const totalShipping = Number(formData["Total Shipping"]) || 0;
  const notes = formData["Notes"] || "";
  const invoiceNumber = (formData["Invoice Number"] || "").trim();

  // Invoice file: read the hidden inputs; if a freshly-attached file is still a
  // data URL, push it to Storage now and keep the returned path.
  const invoiceName = formData["Invoice File Name"] || "";
  const invoiceType = formData["Invoice File Type"] || "";
  let invoiceFile = formData["Invoice File"] || "";
  if (invoiceFile && typeof invoiceFile === "string" && invoiceFile.startsWith("data:")) {
    try {
      toast("Uploading invoice…");
      invoiceFile = await uploadReceiptDataUrl(invoiceFile, invoiceName, invoiceType);
    } catch (e: any) {
      toast("Invoice upload failed: " + (e?.message || e), "err");
      return;
    }
  }
  // On edit, evict a replaced/cleared previously-stored invoice file.
  if (origId) {
    const prevR = DB.table("grading").find(r => r["Submission ID"] === origId);
    const oldRef = prevR?.["Invoice File"];
    if (isStoragePath(oldRef) && oldRef !== invoiceFile) deleteReceiptByPath(oldRef as string).catch(() => {});
  }

  const cards = (s.Cards || []).filter(c => c["Card Lot ID"] || c["Card Name"]);

  if (!cards.length) { toast("Add at least one card to the submission.", "err"); return; }
  if (!s["Service Types"].length) { toast("Pick a service type (Grading and/or Repair).", "err"); return; }

  const totalFees = cards.reduce((acc, c) => acc + (Number(c["Service Fee"]) || 0), 0);
  const totalPre  = cards.reduce((acc, c) => acc + (Number(c["Pre-FMV"]) || 0), 0);
  const totalPost = cards.reduce((acc, c) => acc + (Number(c["Post-FMV"]) || 0), 0);

  // Reverse the previous footprint, then re-apply. Grading is a state
  // progression (At Service -> Returned -> Listed/Sold), so each save undoes
  // the prior card-field changes and re-applies cleanly — keeping each card's
  // captured "before" snapshot anchored to its true pre-grading state. The
  // orphan guard blocks if a graded card was sold/traded in the meantime.
  const isNew = !origId;
  const existing = isNew ? null : DB.table("grading").find(r => r["Submission ID"] === origId);
  // Only re-touch card state when a card-affecting field changed. Pure meta
  // edits (notes, provider, dates) skip the reverse-reapply.
  let applyCardChanges = isNew;
  if (!isNew) {
    if (gradingCardsChanged(existing, status, totalShipping, s["Service Types"], cards)) {
      const oldEffect = effectForTransaction("grading", existing);
      const res = reverseEffect(oldEffect, { excludeId: origId });
      if (!res.ok) { toast("Can't edit: " + res.blockedBy + " — handle that first.", "err"); return; }
      applyCardChanges = true;
    }
  }

  // Service Fee + (Total Shipping / N) capitalizes into each card's basis once
  // the cards come back graded — costs to make the asset saleable.
  const shipPerCard = cards.length > 0 ? +((totalShipping || 0) / cards.length).toFixed(2) : 0;
  const gradedStatuses = ["Returned", "Listed", "Sold"];
  let effect: InvEffect = [];
  let updatedSourceLots = 0;

  if (applyCardChanges) for (const c of cards) {
    if (!c["Card Lot ID"]) continue;
    const src = DB.table("cards").find(r => r["Card Lot ID"] === c["Card Lot ID"]);
    if (!src) continue;
    const qty = Number(src["Qty"]) || 1;

    // Snapshot every field grading may overwrite so delete/edit can restore it.
    const before: Record<string, any> = {
      "Status":           src["Status"],
      "Condition/Grade":  src["Condition/Grade"],
      "Inventory Class":  src["Inventory Class"],
      "Current FMV":      src["Current FMV"],
      "Valuation Source": src["Valuation Source"],
      "Unit Basis":       src["Unit Basis"],
      "Total Basis":      src["Total Basis"],
      "Unrealized G/L":   src["Unrealized G/L"],
    };

    const patch: Record<string, any> = {};
    if (status === "At Service") {
      patch["Status"] = s["Service Types"].includes("Grading") ? "At Grading" : "Repair";
    } else if (gradedStatuses.includes(status)) {
      // Card came back graded — apply grade/class/FMV/basis regardless of which
      // post-grading status it's in (Returned / Listed / Sold all keep these).
      // A returned card goes straight back to sellable inventory — the grading
      // history lives on this submission + the lot history, not in Status.
      patch["Status"] = status === "Returned" ? "Available" : status;
      if (c["Grade"]) {
        patch["Condition/Grade"] = c["Grade"];
        patch["Inventory Class"] = "Slab";
      }
      const addition = +((Number(c["Service Fee"]) || 0) + shipPerCard).toFixed(2);
      const prevTotalBasis = Number(src["Total Basis"]) || 0;
      const newTotalBasis  = +(prevTotalBasis + addition).toFixed(2);
      if (addition !== 0) {
        patch["Total Basis"] = newTotalBasis;
        patch["Unit Basis"]  = qty > 0 ? +(newTotalBasis / qty).toFixed(2) : 0;
      }
      let fmvAfter = Number(src["Current FMV"]) || 0;
      if (Number(c["Post-FMV"]) > 0) {
        fmvAfter = +((Number(c["Post-FMV"]) || 0) * qty).toFixed(2);
        patch["Current FMV"]      = fmvAfter;
        patch["Valuation Source"] = "Manual";
      }
      patch["Unrealized G/L"] = +(fmvAfter - (addition !== 0 ? newTotalBasis : prevTotalBasis)).toFixed(2);
    } else {
      patch["Status"] = status;
    }

    if (Object.keys(patch).length) {
      DB.update("cards", "Card Lot ID", c["Card Lot ID"]!, patch);
      effect.push({ kind: "lotFieldChange", table: "cards", lotId: c["Card Lot ID"]!, before });
      updatedSourceLots++;
    }
  }
  else {
    // Meta-only edit — keep the submission's existing footprint.
    effect = effectForTransaction("grading", existing);
  }

  const subRow = {
    "Submission ID":     submissionId,
    "Date Sent":         dateSent,
    "Date Returned":     dateReturned,
    "Status":            status,
    "Service Types":     s["Service Types"].slice(),
    "Provider":          provider,
    "Total Shipping":    totalShipping,
    "Invoice Number":    invoiceNumber,
    "Invoice File":      invoiceFile,
    "Invoice File Name": invoiceName,
    "Invoice File Type": invoiceType,
    "Cards":             cards,
    "Total Service Fees": +totalFees.toFixed(2),
    "Total Pre-FMV":      +totalPre.toFixed(2),
    "Total Post-FMV":     +totalPost.toFixed(2),
    "Inv Effect":        effect,
    "Notes":             notes,
  };
  if (origId) DB.update("grading", "Submission ID", origId, subRow);
  else        DB.add("grading", subRow);

  // Optional embedded mileage trip — links to this submission via Related
  // Deal/Lot. Dated to Date Sent (the drop-off / ship-out drive).
  await saveMileageSection(submissionId, dateSent);

  delete (window as any)._gradingState;
  toast(updatedSourceLots ? `Submission saved · ${updatedSourceLots} card lot(s) updated` : "Submission saved");
  closeModal();
  render();
}

/* ---------------- Invoice upload/view/remove (in-form) ---------------- */
async function onGradingInvoiceUpload(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    const result = await processReceiptFile(file);
    const form = document.getElementById("gradingForm") as HTMLFormElement | null;
    if (!form) return;
    (form.querySelector('[name="Invoice File"]')      as HTMLInputElement).value = result.dataUrl;
    (form.querySelector('[name="Invoice File Name"]') as HTMLInputElement).value = result.name;
    (form.querySelector('[name="Invoice File Type"]') as HTMLInputElement).value = result.type;
    document.getElementById("gradingInvoiceUI")!.innerHTML = renderGradingInvoiceUI({
      "Invoice File": result.dataUrl, "Invoice File Name": result.name, "Invoice File Type": result.type,
    });
    markModalDirty();
    toast("Invoice attached — click Save to commit");
  } catch (err: any) {
    toast("Upload failed: " + (err?.message || err), "err");
  }
}

function clearGradingInvoice(): void {
  const form = document.getElementById("gradingForm") as HTMLFormElement | null;
  if (!form) return;
  (form.querySelector('[name="Invoice File"]')      as HTMLInputElement).value = "";
  (form.querySelector('[name="Invoice File Name"]') as HTMLInputElement).value = "";
  (form.querySelector('[name="Invoice File Type"]') as HTMLInputElement).value = "";
  document.getElementById("gradingInvoiceUI")!.innerHTML = renderGradingInvoiceUI({});
  markModalDirty();
}

async function viewGradingInvoiceFromForm(): Promise<void> {
  const form = document.getElementById("gradingForm") as HTMLFormElement | null;
  if (!form) return;
  const fileRef = (form.querySelector('[name="Invoice File"]')      as HTMLInputElement).value;
  const type    = (form.querySelector('[name="Invoice File Type"]') as HTMLInputElement).value;
  const name    = (form.querySelector('[name="Invoice File Name"]') as HTMLInputElement).value;
  await viewReceipt(fileRef, type, name);
}

/** View the stored invoice for a submission straight from the Grading table. */
export async function viewInvoiceForGrading(id: string): Promise<void> {
  const row = DB.table("grading").find(r => r["Submission ID"] === id);
  if (!row || !row["Invoice File"]) { toast("No invoice attached", "err"); return; }
  await viewReceipt(row["Invoice File"], row["Invoice File Type"], row["Invoice File Name"]);
}

// Inline-handler bridges
(window as any).onGradingInvoiceUpload     = onGradingInvoiceUpload;
(window as any).clearGradingInvoice        = clearGradingInvoice;
(window as any).viewGradingInvoiceFromForm = viewGradingInvoiceFromForm;
(window as any).viewInvoiceForGrading      = viewInvoiceForGrading;
(window as any).editGrading              = editGrading;
(window as any).saveGrading              = saveGrading;
(window as any).toggleGradingService     = toggleGradingService;
(window as any).addGradingCard           = addGradingCard;
(window as any).removeGradingCard        = removeGradingCard;
(window as any).updateGradingCardField   = updateGradingCardField;
(window as any).updateGradingProvider    = updateGradingProvider;
